<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  SECTION_TEMPLATES,
  buildSectionDragData,
} from 'dashboard/routes/dashboard/appBuilder/sectionTemplates';

const emit = defineEmits(['addBlock', 'addSection']);

const { t } = useI18n();

// Dynamic blocks render rows from a bound data source (the moat applies).
// Static blocks render fixed content with no data binding.
const PALETTE_SECTIONS = [
  {
    key: 'dynamic',
    accent: 'teal',
    labelKey: 'APP_BUILDER.PALETTE.DYNAMIC',
    hintKey: 'APP_BUILDER.PALETTE.DYNAMIC_HINT',
    items: [
      {
        block_type: 'list',
        dynamic: true,
        labelKey: 'APP_BUILDER.PALETTE.LIST',
        descKey: 'APP_BUILDER.PALETTE.LIST_DESC',
        icon: 'M4 6h16M4 10h16M4 14h16M4 18h16',
      },
      {
        block_type: 'table',
        dynamic: true,
        labelKey: 'APP_BUILDER.PALETTE.TABLE',
        descKey: 'APP_BUILDER.PALETTE.TABLE_DESC',
        icon: 'M3 10h18M3 14h18M3 6h18M3 18h18M9 6v12M15 6v12',
      },
      {
        block_type: 'item_details',
        dynamic: true,
        labelKey: 'APP_BUILDER.PALETTE.DETAIL',
        descKey: 'APP_BUILDER.PALETTE.DETAIL_DESC',
        icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
      },
      {
        block_type: 'summary_card',
        dynamic: true,
        labelKey: 'APP_BUILDER.PALETTE.STAT',
        descKey: 'APP_BUILDER.PALETTE.STAT_DESC',
        icon: 'M3 3v18h18M7 14l4-4 4 4 5-5',
      },
    ],
  },
  {
    key: 'static',
    accent: 'violet',
    labelKey: 'APP_BUILDER.PALETTE.STATIC',
    hintKey: 'APP_BUILDER.PALETTE.STATIC_HINT',
    items: [
      {
        block_type: 'header',
        dynamic: false,
        labelKey: 'APP_BUILDER.PALETTE.HEADING',
        descKey: 'APP_BUILDER.PALETTE.HEADING_DESC',
        icon: 'M4 6h16M4 12h10M4 18h7',
      },
      {
        block_type: 'hero',
        dynamic: false,
        labelKey: 'APP_BUILDER.PALETTE.HERO',
        descKey: 'APP_BUILDER.PALETTE.HERO_DESC',
        icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6z',
      },
      {
        block_type: 'cta',
        dynamic: false,
        labelKey: 'APP_BUILDER.PALETTE.CTA',
        descKey: 'APP_BUILDER.PALETTE.CTA_DESC',
        icon: 'M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122',
      },
      {
        block_type: 'quick_links',
        dynamic: false,
        labelKey: 'APP_BUILDER.PALETTE.QUICK_LINKS',
        descKey: 'APP_BUILDER.PALETTE.QUICK_LINKS_DESC',
        icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101M10.172 13.828a4 4 0 005.656 0l4-4a4 4 0 10-5.656-5.656l-1.1 1.1',
      },
    ],
  },
];

const ACCENT_STYLES = {
  teal: {
    card: 'border-teal-500/25 hover:bg-teal-500/5 hover:border-teal-500/40',
    iconBg: 'bg-teal-500/10',
    iconText: 'text-teal-500',
  },
  violet: {
    card: 'border-violet-500/25 hover:bg-violet-500/5 hover:border-violet-500/40',
    iconBg: 'bg-violet-500/10',
    iconText: 'text-violet-500',
  },
};

function accentStyles(section) {
  return ACCENT_STYLES[section.accent] || ACCENT_STYLES.teal;
}

function buildBlockDefaults(item) {
  return {
    block_type: item.block_type,
    dynamic: item.dynamic,
    config: {},
    field_mapping: {},
    source_filter: [],
    allowed_group_ids: [],
    // Bare enum value — PortalBlock.visibility enum stores 'public'/'logged_in'/'group'
    // (the _prefix only namespaces the generated methods, not the assignable values).
    visibility: 'public',
  };
}

function handleDragStart(e, item) {
  e.dataTransfer.setData(
    'block-data',
    JSON.stringify(buildBlockDefaults(item))
  );
  e.dataTransfer.effectAllowed = 'copy';
}

function handleClick(item) {
  emit('addBlock', buildBlockDefaults(item));
}

const sections = ref(PALETTE_SECTIONS);

// Top-level palette tabs: raw primitives ('blocks') vs pre-built drop-and-go bundles ('sections').
const PALETTE_TABS = [
  { key: 'blocks', labelKey: 'APP_BUILDER.PALETTE.BLOCKS_TAB' },
  { key: 'sections', labelKey: 'APP_BUILDER.PALETTE.SECTIONS_TAB' },
];
const activeTab = ref('blocks');

const sectionTemplates = ref(SECTION_TEMPLATES);

function handleSectionDragStart(e, template) {
  e.dataTransfer.setData(
    'section-data',
    JSON.stringify(buildSectionDragData(template))
  );
  e.dataTransfer.effectAllowed = 'copy';
}

function handleSectionClick(template) {
  emit('addSection', template);
}
</script>

<template>
  <div class="w-52 border-r border-n-weak bg-n-slate-1 shrink-0 flex flex-col">
    <div class="p-2 border-b border-n-weak">
      <div class="flex gap-1 p-0.5 rounded-lg bg-n-slate-3">
        <button
          v-for="tab in PALETTE_TABS"
          :key="tab.key"
          type="button"
          class="flex-1 text-xs font-medium px-2 py-1 rounded-md transition-colors"
          :class="
            activeTab === tab.key
              ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          @click="activeTab = tab.key"
        >
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
          {{ t(tab.labelKey) }}
        </button>
      </div>
    </div>
    <div
      v-show="activeTab === 'blocks'"
      class="flex-1 overflow-y-auto p-2 space-y-1"
    >
      <template v-for="section in sections" :key="section.key">
        <div
          class="px-2 pt-1.5 pb-1"
          :class="{ 'mt-2': section.key !== 'dynamic' }"
        >
          <div
            class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
          >
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
            {{ t(section.labelKey) }}
          </div>
          <div class="text-[10px] text-n-slate-9 normal-case mt-0.5">
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
            {{ t(section.hintKey) }}
          </div>
        </div>
        <div
          v-for="item in section.items"
          :key="item.block_type"
          class="rounded-lg p-2.5 border cursor-grab flex items-center gap-2.5 transition-all"
          :class="accentStyles(section).card"
          draggable="true"
          @dragstart="handleDragStart($event, item)"
          @click="handleClick(item)"
        >
          <div
            class="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
            :class="accentStyles(section).iconBg"
          >
            <svg
              class="w-3.5 h-3.5"
              :class="accentStyles(section).iconText"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path :d="item.icon" />
            </svg>
          </div>
          <div>
            <div class="text-xs font-medium text-n-slate-12">
              <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
              {{ t(item.labelKey) }}
            </div>
            <div class="text-[10px] text-n-slate-10">
              <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
              {{ t(item.descKey) }}
            </div>
          </div>
        </div>
      </template>
    </div>
    <div
      v-show="activeTab === 'sections'"
      class="flex-1 overflow-y-auto p-2 space-y-1"
    >
      <div class="px-2 pt-1.5 pb-1">
        <div class="text-[10px] text-n-slate-9 normal-case">
          {{ t('APP_BUILDER.PALETTE.SECTIONS_HINT') }}
        </div>
      </div>
      <div
        v-for="template in sectionTemplates"
        :key="template.id"
        class="rounded-lg p-2.5 border cursor-grab flex items-center gap-2.5 transition-all border-amber-500/25 hover:bg-amber-500/5 hover:border-amber-500/40"
        draggable="true"
        @dragstart="handleSectionDragStart($event, template)"
        @click="handleSectionClick(template)"
      >
        <div
          class="w-7 h-7 rounded-md flex items-center justify-center shrink-0 bg-amber-500/10"
        >
          <svg
            class="w-3.5 h-3.5 text-amber-500"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path :d="template.icon" />
          </svg>
        </div>
        <div>
          <div class="text-xs font-medium text-n-slate-12">
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
            {{ t(template.labelKey) }}
          </div>
          <div class="text-[10px] text-n-slate-10">
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
            {{ t(template.descKey) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
